import { AutogenLayoutStateTaxTable } from "./autogen/AutogenLayoutStateTaxTable";
import { Currency, FieldUpdateEvent } from "@mcleod/core";
import {
    ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Layout,
    LookupModelSearchEvent, LookupModelSelectionEvent, SlideoutDecorator, TableAction, TableContentsChangedEvent,
    Textbox
} from "@mcleod/components";
import { DuplicateId } from "@mcleod/dispatch";
import { CommonDialogs } from "@mcleod/common";

export class StateTaxTable extends AutogenLayoutStateTaxTable {
    override onLoad() {
        DuplicateId.configureIdTextbox(this.textboxId, {
            tableName: "statetaxtable",
            screenName: this.titleSingular,
            navigateToUrl: `${this.layoutName}?mode=update&key=`
        });
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            let ignoreChange: boolean = false;
            this.buttonVersion.visible = event.newMode !== DataSourceMode.SEARCH;
            if (event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.ADD) {
                this.activeRow.addAfterFieldUpdateListener(async (event: FieldUpdateEvent) => {
                    if (event.newValue != event.oldValue && event.fieldName === "type_of") {
                        if (ignoreChange) {
                            return;
                        }
                        ignoreChange = true; // Prevent further updates
                        let userConfirmed = true;
                        if (this.hasAdditionalTaxRecords()) {
                            userConfirmed = await this.promptUserForAdditionalTaxDeletion("Type");
                        }
                        if (!userConfirmed) {
                            // Revert the change
                            this.activeRow.set(event.fieldName, event.oldValue);
                        } else {
                            // Delete additional tax records
                            await this.deleteAdditionalTaxRecords();
                            this.clearAutoTablesFields(event.fieldName);
                        }
                        ignoreChange = false; // Reset ignoreChange for future updates
                    }
                    if (event.fieldName === "use_auto_tax_table" || event.fieldName === "type_of") {
                        this.setComponentVisibility();
                        this.setAccountsRequired();
                    }
                    if (event.fieldName === "id" || event.fieldName === "tax_table_income") {
                        this.setAddAdditionalTaxesButtonEnabled();
                    }
                    if (event.fieldName === "employer_sui_pct") {
                        this.textboxSuiExpGlid.required = !this.activeRow.isNull("employer_sui_pct");
                    }
                    if (event.fieldName === "payee_sui_pct") {
                        this.textboxSdiLiabGlid.required = !this.activeRow.isNull("payee_sui_pct");
                    }
                    if (event.fieldName === "employer_sdi_aux") {
                        this.textboxSdiExpGlid.required = !this.activeRow.isNull("employer_sdi_aux");
                    }
                    if (event.fieldName === "payee_sdi_pct") {
                        this.textboxSdiLiabGlid.required = !this.activeRow.isNull("payee_sdi_pct");
                    }
                    if (event.fieldName === "employer_ett_pct") {
                        this.textboxEttExpGlid.required = !this.activeRow.isNull("employer_ett_pct");
                    }
                    const GlAccountsRequiredFields = ["employer_sui_pct", "payee_sui_pct", "employer_sdi_aux", "payee_sdi_pct", "employer_ett_pct", "tax_table_payee_sui", "tax_table_unemployment", "tax_table_employer_sdi", "tax_table_sdi", "tax_table_ett", "bonus_tax_rate", "tax_table_income"];
                    if (GlAccountsRequiredFields.includes(event.fieldName)) {
                        this.setAccountsRequired();
                    }
                });
                this.setComponentVisibility();
                this.setTaxInstructions();
                this.setAddAdditionalTaxesButtonEnabled();
                this.setAccountsRequired();
                this.layoutSingleTaxTable.tableStateTaxDetail.addContentsChangedListener(this.stateTaxDetailContentsChanged.bind(this));
                this.layoutMarriedTaxTable.tableStateTaxDetail.addContentsChangedListener(this.stateTaxDetailContentsChanged.bind(this));
                this.layoutHeadOfHouseholdTaxTable.tableStateTaxDetail.addContentsChangedListener(this.stateTaxDetailContentsChanged.bind(this));
                this.buttonVersion.addClickListener((event: ClickEvent) => {
                    const layout = Layout.getLayout("lme/settlement/TaxControlVersion");
                    SlideoutDecorator.decorateAndSlideIn(layout, {
                        title: "Tax Control Version",
                        fillVerticalSpace: true,
                        width: "40%"
                    });
                });
            }
        });

        // Setup lookup model search listener for income tax table
        this.setupIncomeTaxTableLookup();

        // Setup lookup model search listeners for dependent tax tables
        this.setupDependentTaxTableLookups();
        this.layoutStateAdditionalTaxesTable.taxTableBeforeLookupSearch = this.dependentTaxTableBeforeLookupSearch.bind(this);
        this.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.UPDATE || event.getAction() === DataSourceAction.ADD) {
                if (this.activeRow.isNull("wh_allow_amt") && this.activeRow.getBoolean("use_auto_tax_table", false)) {
                    this.activeRow.set("wh_allow_amt", new Currency(0));
                }
            }
        });
    }

    private stateTaxDetailContentsChanged(event: TableContentsChangedEvent): void {
        if (event.getAction() === TableAction.DELETE || event.getAction() === TableAction.ADD) {
            this.setAccountsRequired();
        }
    }

    /**
     * Sets up the lookup model search listener for the income tax table
     */
    private setupIncomeTaxTableLookup(): void {
        this.textboxTaxTableIncome.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            event.filter.tax_type = this.activeRow.get("type_of");
            event.filter.is_income_tax_table = true;
            if (!this.textboxTaxTableIncome.validateSimple(true, false)) {
                event.filter.search_filter = this.textboxTaxTableIncome.text;
            }
        });

        // Add selection listener to set tooltip with instructions
        this.textboxTaxTableIncome.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.SEARCH) {
                return;
            }
            if (event.hasSelection()) {
                if (this.hasAdditionalTaxRecords()) {
                    const userConfirmed = await this.promptUserForAdditionalTaxDeletion("Income Tax Table");
                    if (!userConfirmed) {
                        // Revert the change
                        const oldValue = event.deselection?.get("name") ?? this.activeRow.originalData["tax_table_income"];
                        this.activeRow.setValueAndLookupModelData("tax_table_income", oldValue, { name: oldValue });
                        const oldInstructions = event.deselection?.get("description") ?? this.activeRow.originalData["tax_table_income_instructions"];
                        this.textboxTaxTableIncome.tooltipDynamicContent = oldInstructions;
                    } else {
                        // Delete additional tax records
                        await this.deleteAdditionalTaxRecords();
                        this.clearAutoTablesFields("tax_table_income");
                        const instructions = event.selection.get("description");
                        if (instructions) {
                            this.textboxTaxTableIncome.tooltipDynamicContent = instructions;
                        }
                    }
                } else {
                    this.clearAutoTablesFields("tax_table_income");
                    const instructions = event.selection.get("description");
                    if (instructions) {
                        this.textboxTaxTableIncome.tooltipDynamicContent = instructions;
                    }
                }
            }
        });
    }

    /**
     * Sets up lookup model search listeners for all tax tables that depend on income tax table
     */
    private setupDependentTaxTableLookups(): void {
        const taxTableComponents: { component: Textbox, validationMessage: string }[] = [
            {
                component: this.textboxTaxTableUnemployment,
                validationMessage: "Please select set Income Tax Table before selecting an employer SUI tax table"
            },
            {
                component: this.textboxTaxTablePayeeSUI,
                validationMessage: "Please select set Income Tax Table before selecting a payee SUI tax table"
            },
            {
                component: this.textboxTaxTableEmployerSdi,
                validationMessage: "Please select set Income Tax Table before selecting an employer SDI tax table"
            },
            {
                component: this.textboxTaxTableSdi,
                validationMessage: "Please select set Income Tax Table before selecting a payee SDI tax table"
            },
            {
                component: this.textboxTaxTableEtt,
                validationMessage: "Please select set Income Tax Table before selecting an ETT tax table"
            }
        ];

        // Add lookup model search listener to each component
        taxTableComponents.forEach(({ component, validationMessage }) => {
            component.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                this.dependentTaxTableBeforeLookupSearch(event, validationMessage);
            });

            // Add selection listener to set tooltip with instructions
            component.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
                const instructions = event.selection?.get("description");
                if (instructions) {
                    component.tooltipDynamicContent = instructions;
                    if (component === this.textboxTaxTableEmployerSdi) {
                        this.textboxEmployerSdiAux.tooltipDynamicContent = instructions;
                    }
                }
            });
        });
    }

    private dependentTaxTableBeforeLookupSearch(event: LookupModelSearchEvent, validationMessage): void {
        const component = event.target;
        // Check if income tax table is set
        if (this.activeRow.isNull("tax_table_income")) {
            event.stop();
            component.validationWarning = validationMessage;
            return;
        }

        // Apply common filters
        event.filter.tax_type = this.activeRow.get("type_of");
        if (!component.validateSimple(true, false)) {
            event.filter.search_filter = component.text;
        }
        event.filter.state_filter = this.activeRow.get("tax_table_income");
    }

    setAddAdditionalTaxesButtonEnabled() {
        this.layoutStateAdditionalTaxesTable.buttonAddAdditionalTaxes.enabled = !this.activeRow.isNull("id") && !this.activeRow.isNull("tax_table_income");
        if (!this.layoutStateAdditionalTaxesTable.buttonAddAdditionalTaxes.enabled) {
            this.layoutStateAdditionalTaxesTable.buttonAddAdditionalTaxes.disabledTooltip = "Please enter a Tax Table Code and select an Income Tax Table before adding additional taxes.";
        }
    }

    setTaxInstructions() {
        this.textboxTaxTableUnemployment.tooltipDynamicContent = this.activeRow.get("tax_table_unemployment_instructions");
        this.textboxTaxTablePayeeSUI.tooltipDynamicContent = this.activeRow.get("tax_table_payee_sui_instructions");
        this.textboxTaxTableEmployerSdi.tooltipDynamicContent = this.activeRow.get("tax_table_employer_sdi_instructions");
        this.textboxTaxTableSdi.tooltipDynamicContent = this.activeRow.get("tax_table_sdi_instructions");
        this.textboxEmployerSdiAux.tooltipDynamicContent = this.activeRow.get("tax_table_employer_sdi_instructions") ?? this.activeRow.get("tax_table_sdi_instructions"), null, false;
        this.textboxTaxTableEtt.tooltipDynamicContent = this.activeRow.get("tax_table_ett_instructions");
        this.textboxTaxTableIncome.tooltipDynamicContent = this.activeRow.get("tax_table_income_instructions");
    }

    setComponentVisibility() {
        const isManual = this.activeRow.getBoolean("use_auto_tax_table", false) === false;
        const isStateTableType = this.activeRow.get("type_of") === "S";
        this.tabAutoTables.caption = this.activeRow.getBoolean("use_auto_tax_table", false) ? "Auto Tables" : "Deductions";
        this.tabWithholdingTables.visible = isManual;
        this.textboxWhAllowAmt.required = this.tabWithholdingTables.visible;
        this.switchAllowanceType.required = this.tabWithholdingTables.visible;
        this.tabAdditionalTaxes.visible = !isManual;

        // General
        this.checkboxDeductFica.visible = isManual;
        this.textboxMaxFicaWages.visible = isManual && this.checkboxDeductFica.checked;
        this.textboxMaxAdjMarried.visible = isManual;
        this.textboxMaxAdjSingle.visible = isManual;
        this.textboxStdAdjPct.visible = isManual;
        this.textboxTaxCalcMethod.visible = isManual;
        this.textboxTaxCalcMethod.required = isManual;
        this.checkDeductFed.visible = isStateTableType;
        this.textboxTaxTableIncome.visible = !isManual;
        this.textboxTaxTableIncome.required = !isManual;

        // SUI
        this.panelSUI.visible = isStateTableType || isManual;
        this.textboxTaxTablePayeeSUI.visible = !isManual;
        this.textboxTaxTableUnemployment.visible = !isManual;
        this.textboxPayeeSuiPct.visible = isManual;
        this.textboxEmployeeMaxSui.visible = isManual;
        this.textboxEmployerMaxSui.visible = isManual;

        // SDI
        this.panelSDI.visible = isStateTableType || isManual;
        this.textboxTaxTableEmployerSdi.visible = !isManual;
        this.textboxEmployerSdiAux.caption = isManual ? "Employer SDI %" : "Auxiliary";
        this.textboxTaxTableSdi.visible = !isManual;
        this.textboxPayeeSdiPct.visible = isManual;
        this.textboxMaxEmployerSdi.visible = isManual;
        this.textboxMaxSdi.visible = isManual;

        // ETT
        this.panelETT.visible = isStateTableType || isManual;
        this.textboxTaxTableEtt.visible = !isManual;
        this.textboxEmployerMaxEtt.visible = isManual;
        this.textboxEmployerEttPct.visible = isManual;

        // GL Accounts
        this.textboxSuiExpGlid.visible = isStateTableType || isManual;
        this.textboxSuiLiabGlid.visible = isStateTableType || isManual;
        this.textboxSdiExpGlid.visible = isStateTableType || isManual;
        this.textboxSdiLiabGlid.visible = isStateTableType || isManual;
        this.textboxEttExpGlid.visible = isStateTableType || isManual;
        this.textboxEttLiabGlid.visible = isStateTableType || isManual;
    }

    private setAccountsRequired() {
        const isManual = this.activeRow.getBoolean("use_auto_tax_table", false) === false;
        this.textboxSuiExpGlid.required = !this.activeRow.isNull("employer_sui_pct");
        this.textboxSuiLiabGlid.required = isManual ? !this.activeRow.isNull("payee_sui_pct") || !this.activeRow.isNull("employer_sui_pct") : !this.activeRow.isNull("tax_table_payee_sui") || !this.activeRow.isNull("tax_table_unemployment") || !this.activeRow.isNull("employer_sui_pct");
        this.textboxSdiExpGlid.required = isManual ? !this.activeRow.isNull("employer_sdi_aux") : !this.activeRow.isNull("employer_sdi_aux") || !this.activeRow.isNull("tax_table_employer_sdi");
        this.textboxSdiLiabGlid.required = isManual ? !this.activeRow.isNull("payee_sdi_pct") || !this.activeRow.isNull("employer_sdi_aux") : !this.activeRow.isNull("employer_sdi_aux") || !this.activeRow.isNull("tax_table_sdi") || !this.activeRow.isNull("tax_table_employer_sdi");
        this.textboxEttExpGlid.required = isManual ? !this.activeRow.isNull("employer_ett_pct") : !this.activeRow.isNull("tax_table_ett");
        this.textboxEttLiabGlid.required = isManual ? !this.activeRow.isNull("employer_ett_pct") : !this.activeRow.isNull("tax_table_ett");
        const withholdingDataExists = this.sourceSinglestatetaxtable.data.length > 0 || this.sourceMarriedstatetaxtable.data.length > 0 || this.sourceHeadofhouseholdstatetaxtable.data.length > 0;
        this.textboxTaxWhGlid.required = isManual ? !this.activeRow.isNull("bonus_tax_rate") || withholdingDataExists : this.textboxTaxTableIncome.required;
    }

    /**
     * Checks if there are any additional tax records in the StateAdditionalTaxesTable
     */
    private hasAdditionalTaxRecords(): boolean {
        return this.layoutStateAdditionalTaxesTable.tableAdditionalTaxes.dataSource.data.length > 0;
    }

    /**
     * Prompts the user about deleting additional tax records when Type or Income Tax Table changes
     */
    private async promptUserForAdditionalTaxDeletion(fieldName: string): Promise<boolean> {
        const message = `The ${fieldName} has changed and there are additional tax records. Continuing will delete the additional tax records. Do you want to proceed?`;
        return await CommonDialogs.showYesNo(message, "Delete Additional Tax Records");
    }

    /**
     * Deletes all additional tax records from the StateAdditionalTaxesTable
     */
    private async deleteAdditionalTaxRecords(): Promise<void> {
        // Delete the additional tax records
        for (let i = this.layoutStateAdditionalTaxesTable.tableAdditionalTaxes.rows.length - 1; i >= 0; i--) {
            const row = this.layoutStateAdditionalTaxesTable.tableAdditionalTaxes.rows[i];
            this.layoutStateAdditionalTaxesTable.tableAdditionalTaxes.deleteRow(row.index);
        }
    }

    /**
     * Clears all Auto Tables field values except the triggering field
     * @param triggeringField The field that triggered the change (to avoid clearing it)
     */
    private clearAutoTablesFields(triggeringField: string): void {
        // Clear all Auto Tables fields (fields that are visible when use_auto_tax_table is true)
        // except the field that triggered the change
        const autoTablesFields = [
            "tax_table_income",
            "tax_table_payee_sui",
            "tax_table_unemployment",
            "tax_table_employer_sdi",
            "tax_table_sdi",
            "tax_table_ett",
            "employer_sdi_aux",
            "employer_sui_pct"
        ];

        autoTablesFields.forEach(fieldName => {
            // Don't clear the field that triggered the change (except for type_of which should clear all)
            if (triggeringField === "type_of" || fieldName !== triggeringField) {
                this.activeRow.set(fieldName, null);
            }
        });
    }
}
